import { RoseTree } from '@tableau/data-structures';
import {
  NestedProjectBasicInfo,
  NestedProjectExplorer,
  NestedProjectsFractalRowStore,
  NestedProjectsTreeHelper,
} from '@tableau/nested-project-explorer';
import * as _ from 'underscore';

import { IActionContext } from '../helpers/ActionContext';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { DestinationProjectMapper } from '../mappers/DestinationProjectMapper';
import {
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  IContentType,
  ProjectContentType,
  WorkbookContentType
} from '../models/ContentTypes';
import { FlowBaseInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IDataRoleBase } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { ISliceable } from '../models/ISliceable';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { DestinationProjectContentType, DestinationProjects } from '../resources/DestinationProjects';
import { Flows } from '../resources/Flows';
import { Projects } from '../resources/Projects';
import { Workbooks } from '../resources/Workbooks';
import { DestinationProjectStatus } from '../ServerApi/types/IDestinationProject';
import { IProjectName } from '../ServerApi/types/IProject';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import {
  ContentActionNotification,
  IMultipleResultNotificationOptions,
  INotificationTranslationIds,
  MultipleResultNotificationOptions,
} from './ContentActionNotification';
import { DataRoleErrors } from './DataRoleErrors';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { ModalService } from './ModalService';
import { ProjectErrors } from './ProjectErrors';
import { ServerService } from './ServerService';
import { WorkbookErrors } from './WorkbookErrors';

interface ITranslationIds extends INotificationTranslationIds {
  promptId: string;
  successId: string;
  errorId: string;
  dialogTitle: string;
  confirmationText: string;
}

export interface IConfirmMoveToProjectScope extends ng.IScope {
  prompt: string;
  projectPicker: {
    projectsQuery: (query: string) => ISliceable<IProjectName>;
    targetProject: IProjectName;
  };
  projectExplorer: {
    props: NestedProjectExplorer.Props | null;
    selectedProject: NestedProjectBasicInfo | null;
  };
}

export interface IResultWithDestinationProject extends IResult {
  destinationProjectId: string;
}

export class MoveToProjectAction {

  public static $inject = [
    'ConfirmActionDialog',
    '$translate',
    'ContentActionNotification',
    'Workbooks',
    'WorkbookErrors',
    'DataSources',
    'DataSourceErrors',
    'DestinationProjects',
    'Projects',
    'ProjectErrors',
    'Flows',
    'FlowErrors',
    'DataRoles',
    'DataRoleErrors',
    'ModalService',
    'ServerService'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private ContentActionNotification: ContentActionNotification,
    private Workbooks: Workbooks,
    private WorkbookErrors: WorkbookErrors,
    private DataSources: DataSources,
    private DataSourceErrors: DataSourceErrors,
    private DestinationProjects: DestinationProjects,
    private Projects: Projects,
    private ProjectErrors: ProjectErrors,
    private Flows: Flows,
    private FlowErrors: FlowErrors,
    private DataRoles: DataRoles,
    private DataRoleErrors: DataRoleErrors,
    private ModalService: ModalService,
    private ServerService: ServerService) {
  }

  public moveProjects(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForProjects',
        successId: 'moveProjectsToProjectSuccess',
        errorId: 'moveProjectsToProjectError',
        dialogTitle: 'moveProject',
        confirmationText: 'confirmMoveProject'
      },
      projects,
      ProjectContentType,
      (projectId: string) => this.Projects.moveToProject(projectId, projects.keys()),
      actionContext);
  }

  public moveWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResultWithDestinationProject> {
    return this.move(
      {
        promptId: 'selectProjectForWorkbooks',
        successId: 'moveWorkbooksToProjectSuccess',
        errorId: 'moveWorkbooksToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      workbooks,
      WorkbookContentType,
      (projectId: string) => this.Workbooks.moveToProject(projectId, workbooks.keys()),
      actionContext);
  }

  public moveDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {

    // Disallow move if all selected datasources are embedded.
    if (datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook))) {
      return this.ModalService.alert({
        titleId: 'moveDatasourcesToProject_cannotMoveAlertTitle',
        messageId: 'moveDatasourcesToProject_cannotMoveAlertMessage'
      }).result;
    }

    return this.move(
      {
        promptId: 'selectProjectForDatasources',
        successId: 'moveDatasourcesToProjectSuccess',
        errorId: 'moveDatasourcesToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      datasources,
      DatasourceContentType,
      (projectId: string) => this.DataSources.moveToProject(projectId, datasources.keys()),
      actionContext);
  }

  public moveFlows(flows: Set<FlowListItemInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForFlows',
        successId: 'moveFlowsToProjectSuccess',
        errorId: 'moveFlowsToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      flows,
      FlowContentType,
      (projectId: string) => this.Flows.moveToProject(projectId, flows.keys()),
      actionContext);
  }

  public moveDataRoles(dataRoles: Set<IDataRoleBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForDataRoles',
        successId: 'moveDataRolesToProjectSuccess',
        errorId: 'moveDataRolesToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      dataRoles,
      DataRoleContentType,
      (projectId: string) => this.DataRoles.moveToProject(projectId, dataRoles.keys()),
      actionContext);
  }

  private move(
    translations: ITranslationIds,
    items: Set<INamedItemInfo>,
    itemType: IContentType,
    moveFn: (projectId: string) => ng.IPromise<any>,
    actionContext: IActionContext): ng.IPromise<IResultWithDestinationProject> {

    const newDialogScope = <IConfirmMoveToProjectScope>actionContext.scope.$new();
    this.initNestedProjectExplorer(newDialogScope, itemType, items.map(item => item.id));

    newDialogScope.prompt = this.$translate.instant(translations.promptId, {
      ITEM_COUNT: items.size(),
      ITEM_NAME: L10n.elideIfLong(items.first().name)
    }, L10n.Interpolations.Count);

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: newDialogScope,
      title: this.$translate.instant(translations.dialogTitle, {
        ITEM_COUNT: items.size()
      }, L10n.Interpolations.Count),
      bodyTemplateUrl: 'moveToProjectDialog.html',
      actionName: this.$translate.instant(translations.confirmationText, {
        ITEM_COUNT: items.size()
      }, L10n.Interpolations.Count),
      action: () => {
        const destinationProjectId = this.getDestinationProjectIdFromDialog(newDialogScope);
        return moveFn(destinationProjectId);
      },
      confirmDisabled: () => {
        return newDialogScope.projectExplorer.selectedProject == null;
      }
    });

    dialogInstance.result.then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(items, result),
          translations,
          itemType,
          newDialogScope.projectExplorer.selectedProject
        )
      );
    });

    return dialogInstance.result
      .then((result: IResultWithDestinationProject) => {
        return {
          ...result,
          destinationProjectId: this.getDestinationProjectIdFromDialog(newDialogScope)
        };

      })
      .finally(() => newDialogScope.$destroy());
  }

  private getDestinationProjectIdFromDialog(dialogScope: IConfirmMoveToProjectScope): string {
    const destinationProjectId = dialogScope.projectExplorer.selectedProject.id;
    return (destinationProjectId === NestedProjectsFractalRowStore.SentinelSiteId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  private initNestedProjectExplorer(scope: IConfirmMoveToProjectScope, itemType: IContentType, itemIds: string[]) {
    // Search queries to be implemented in TFS 645286
    this.getAllProjectsAsTrees('', itemType, itemIds).then(projectTrees => {
      // No scope.$apply here because it's called from within an ng.IPromise callback
      scope.projectExplorer.props = {
        siteName: this.ServerService.sessionSiteName(),
        topLevelDestinationStatus: this.getTopLevelDestinationStatus(itemType),
        projects: projectTrees,
        onSelectedProjectChanged: (project: NestedProjectBasicInfo | null) =>
          scope.$apply(() => scope.projectExplorer.selectedProject = project)
      };
    });

    scope.projectExplorer = {
      props: null,
      selectedProject: null
    };
  }

  private getTopLevelDestinationStatus(itemType: IContentType): DestinationProjectStatus {
    if (itemType !== ProjectContentType) {
      return 'structurally_invalid';
    } else if (!this.ServerService.isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  // This method is separate so it can be mocked in unit tests
  public getAllProjectsAsTrees(
    query: string,
    itemType: IContentType,
    itemIds: string[],
  ): ng.IPromise<RoseTree<NestedProjectBasicInfo>[]> {
    const contentType = <DestinationProjectContentType>itemType.name;
    const projectsQuery = this.DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds,
      query);
    return SliceableHelper.fetchAll(projectsQuery).then(projects => {
      const destinationProjects = DestinationProjectMapper.map(projects.items);
      return NestedProjectsTreeHelper.convertProjectListToTrees(destinationProjects);
    });
  }

  private notification(
    result: MultipleItemResult<INamedItemInfo>,
    translations: ITranslationIds,
    itemType: IContentType,
    project: IProjectName): IMultipleResultNotificationOptions {

    return MultipleResultNotificationOptions.create(result,
      {
        describeSuccessSummary: (successCount: number) =>
          this.$translate.instant(translations.successId, {
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorSummary: (errorCount: number) =>
          this.$translate.instant(translations.errorId, {
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return this.ProjectErrors.errorDetailsFor(<IErrorItem<IProjectBase>>errorItem);
          } else if (itemType === DatasourceContentType) {
            return this.DataSourceErrors.errorDetailsFor(<IErrorItem<IDataSourceBase>>errorItem);
          } else if (itemType === WorkbookContentType) {
            return this.WorkbookErrors.errorDetailsFor(<IErrorItem<IWorkbookBase>>errorItem);
          } else if (itemType === FlowContentType) {
            return this.FlowErrors.errorDetailsFor(<IErrorItem<FlowBaseInfo>>errorItem);
          } else if (itemType === DataRoleContentType) {
            return this.DataRoleErrors.errorDetailsFor(<IErrorItem<IDataRoleBase>>errorItem);
          }
        }
      }
    );
  }

}

Module.instance().service('MoveToProjectAction', MoveToProjectAction);
